import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IError, INSAutoCompleteGroup, INSAutoCompleteOption } from '@norfolk-southern/accessns-components';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import Swal, { SweetAlertResult } from 'sweetalert2';

import { PDFPrintGenerator } from '@ruby/configs/common.constants';
import { NSDiversionsConstants } from '@ruby/configs/diversions.constants';
import { IUserInfo } from '@ruby/modules/account/models/contact.interface';
import { DiversionsCoreActions, DiversionsCoreSelectors } from '@ruby/modules/diversions/store/diversions-core.index';
import { fromDiversionsRequestDetail } from '@ruby/modules/diversions/store/diversions-request-detail.index';
import { DiversionsCancelDialogContainer } from '@ruby/shared/dialogs/diversions/diversions-cancel/containers/diversions-cancel-dialog.container';
import { DiversionsCompleteContainer } from '@ruby/shared/dialogs/diversions/diversions-complete/containers/diversions-complete.container';
import { DiversionsRejectContainer } from '@ruby/shared/dialogs/diversions/diversions-reject/containers/diversions-reject.container';
import { EColors } from '@ruby/shared/enums/colors.enums';
import { EDiversionsActionType, EDiversionsPaths, EDiversionsTypes } from '@ruby/shared/enums/diversions.enum';
import {
  IDiversionsDestTurnoverForm,
  IDiversionsMoves,
  IDiversionsOrigTurnoverForm,
  IDiversionsPrivateCarForm,
  IDiversionsRequestForm,
  IDiversionsRequestSummary,
  IDiversionsReshipForm,
  IDiversionsWaybillDetail
} from '@ruby/shared/models/diversions/diversions.interface';

@Component({
  selector: 'ng-request-detail-container',
  templateUrl: './request-detail.container.html',
  styleUrls: ['./request-detail.container.scss']
})
export class RequestDetailContainer implements OnInit, OnDestroy {
  @ViewChild('printContainer') printContainer!: ElementRef;
  customerNumber!: string;
  customerSequence!: string;
  waybillSerial!: string;
  requestNumber!: string;
  customerType!: string;
  store$: Observable<{
    isLoadingRoads: boolean;
    isLoadingStates: boolean;
    isLoadingWaybillDetail: boolean;
    isLoadingMoves: boolean;
    error?: IError;
    states: Array<INSAutoCompleteGroup>;
    roads: Array<INSAutoCompleteOption>;
    internalRole?: boolean;
    userInfo?: IUserInfo;
    waybillDetail?: IDiversionsWaybillDetail;
    moves: Array<IDiversionsMoves>;
    requestSummary?: IDiversionsRequestSummary;
    rejectSummary?: IDiversionsRequestSummary;
    cancelSummary?: IDiversionsRequestSummary;
    completeSummary?: IDiversionsRequestSummary;
  }>;
  waybillDetail?: IDiversionsWaybillDetail = undefined;
  carMaxUnits = NSDiversionsConstants.carMaxUnits;
  form: UntypedFormGroup = new UntypedFormGroup({});
  rejectDialog?: MatDialogRef<DiversionsRejectContainer>;
  cancelDialog?: MatDialogRef<DiversionsCancelDialogContainer>;
  completeDialog?: MatDialogRef<DiversionsCompleteContainer>;
  private subscription: Subscription = new Subscription();

  constructor(private dialog: MatDialog, private route: ActivatedRoute, private router: Router, private store: Store<fromDiversionsRequestDetail.State>) {
    this.store$ = this.store.select(DiversionsCoreSelectors.selectWaybillDetailState).pipe(
      tap((st: {
        isLoadingRoads: boolean;
        isLoadingStates: boolean;
        isLoadingReject: boolean;
        isLoadingWaybillDetail: boolean;
        isLoadingMoves: boolean;
        error?: IError;
        states: Array<INSAutoCompleteGroup>;
        roads: Array<INSAutoCompleteOption>;
        waybillDetail?: IDiversionsWaybillDetail;
        moves: Array<IDiversionsMoves>;
        internalRole?: boolean;
        userInfo?: IUserInfo;
        requestSummary?: IDiversionsRequestSummary;
        rejectSummary?: IDiversionsRequestSummary;
        cancelSummary?: IDiversionsRequestSummary;
        completeSummary?: IDiversionsRequestSummary;
        params: Params;
      }) => {
        this.waybillDetail = st.waybillDetail;
        this.setupForm();
        if (st.rejectSummary && st.rejectSummary.responseList) {
          this.confirmRejectAction(st.rejectSummary.responseList[0].car);
        }
        if (st.cancelSummary && st.cancelSummary.responseList) {
          this.confirmCancelAction(st.cancelSummary.responseList[0].car);
        }
        if (st.completeSummary && st.completeSummary.responseList) {
          this.confirmCompleteAction(st.completeSummary.responseList[0].car);
        }
      })
    );
  }

  get routesFormArray(): UntypedFormArray {
    return this.form.get('route.routes')! as UntypedFormArray;
  }

  ngOnInit(): void {
    this.subscription = this.route.queryParams.subscribe((queryParams: Params) => {
      this.customerType = queryParams.type;
      this.customerNumber = queryParams.customerNumber;
      this.customerSequence = queryParams.customerSequence;
      this.waybillSerial = queryParams.waybillSerial;
      this.requestNumber = queryParams.requestNumber;
      this.getRequestDetail();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Print Preview
   *
   * @summary Prints the list of patterns
   * @returns void
   */

  // @TODO Fix this
  /* istanbul ignore next */
  printPreview(): void {
    PDFPrintGenerator.print(this.customerType, this.printContainer.nativeElement.innerHTML);
  }

  /**
   * Submit Cancel
   *
   * @summary Submits previous request form
   * @returns void
   */
  submitCancel(): void {
    this.cancelDialog = this.dialog.open(DiversionsCancelDialogContainer, {
      data: {
        customerNumber: this.customerNumber,
        customerSequence: this.customerSequence
      },
      width: '50%'
    });
  }

  /**
   * Submit Reject
   *
   * @summary opens Reject dialog
   * @returns void
   */
  submitReject(): void {
    this.rejectDialog = this.dialog.open(DiversionsRejectContainer, {
      data: {
        customerNumber: this.customerNumber,
        customerSequence: this.customerSequence
      },
      width: '50%'
    });
  }

  /**
   * Submit Complete
   *
   * @summary opens complete dialog
   * @returns void
   */
  submitComplete(): void {
    this.completeDialog = this.dialog.open(DiversionsCompleteContainer, {
      data: {
        customerNumber: this.customerNumber,
        customerSequence: this.customerSequence
      },
      width: '50%'
    });
  }

  /**
   * Navigate To Template
   *
   * @summary Navigate To Template
   * @returns void
   */
  navigateToTemplate(): void {
    let path = '';
    const form: IDiversionsRequestForm | IDiversionsPrivateCarForm | IDiversionsDestTurnoverForm | IDiversionsOrigTurnoverForm | IDiversionsReshipForm = {
      ...this.form.getRawValue(),
      cars: this.form.get('cars')!.value.toString()
    };

    switch (this.customerType) {
      case EDiversionsTypes.DIVERSION:
        path = EDiversionsPaths.REQUEST;
        this.store.dispatch(DiversionsCoreActions.updateForm({ data: { requestForm: form as IDiversionsRequestForm } }));
        break;
      case EDiversionsTypes.EMPTY_CAR_DISPOSITION:
        path = EDiversionsPaths.EMPTY_DISP;
        this.store.dispatch(DiversionsCoreActions.updateForm({ data: { privateCarForm: form as IDiversionsPrivateCarForm } }));
        break;
      case EDiversionsTypes.DESTINATION_TURNOVER:
        path = EDiversionsPaths.DEST_TURNOVER;
        this.store.dispatch(DiversionsCoreActions.updateForm({ data: { destTurnoverForm: form as IDiversionsDestTurnoverForm } }));
        break;
      case EDiversionsTypes.ORIGIN_TURNOVER:
        path = EDiversionsPaths.ORIGIN_TURNOVER;
        this.store.dispatch(DiversionsCoreActions.updateForm({ data: { originTurnoverForm: form as IDiversionsOrigTurnoverForm } }));
        break;
      case EDiversionsTypes.RESHIP:
        path = EDiversionsPaths.RESHIP;
        this.store.dispatch(DiversionsCoreActions.updateForm({ data: { reshipForm: form as IDiversionsReshipForm } }));
        break;
    }
    this.router.navigate([path]);
  }

  /**
   * Confirm Action Dialog
   *
   * @summary Open Action success dialog
   * @params action: string car: string
   * @returns void
   */
  confirmActionDialog(action: string, car: string): void {
    const text = `${ car } : ${ action } Request accepted`;

    Swal.fire({
      title: 'Message',
      text,
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: EColors.GOLD,
      heightAuto: false
    }).then((response: SweetAlertResult) => this.router.navigate(['home/diversions/previous/requests']));
  }

  private confirmRejectAction(carNumber: string): void {
    if (this.rejectDialog) {
      this.rejectDialog.close();
    }
    this.store.dispatch(DiversionsCoreActions.resetRejectSummary());
    this.confirmActionDialog(EDiversionsActionType.REJECT, carNumber);
  }

  private confirmCancelAction(carNumber: string): void {
    if (this.cancelDialog) {
      this.cancelDialog.close();
    }
    this.store.dispatch(DiversionsCoreActions.resetCancelSummary());
    this.confirmActionDialog(EDiversionsActionType.CANCEL, carNumber);
  }

  private confirmCompleteAction(carNumber: string): void {
    if (this.completeDialog) {
      this.completeDialog.close();
    }
    this.store.dispatch(DiversionsCoreActions.resetCompleteSummary());
    this.confirmActionDialog(EDiversionsActionType.COMPLETE, carNumber);
  }

  private getRequestDetail(): void {
    this.store.dispatch(DiversionsCoreActions.loadWaybillDetail({
      data: { customerNumber: this.customerNumber, customerSequence: this.customerSequence }
    }));
  }

  private setupForm(): void {
    switch (this.customerType) {
      case EDiversionsTypes.DIVERSION:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.DIVERSION]();
        this.addJunctions();
        NSDiversionsConstants.addCrossBorder(EDiversionsTypes.DIVERSION, this.form, this.waybillDetail);
        break;
      case EDiversionsTypes.EMPTY_CAR_DISPOSITION:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.EMPTY_CAR_DISPOSITION]();
        this.addJunctions();
        NSDiversionsConstants.addCrossBorder(EDiversionsTypes.EMPTY_CAR_DISPOSITION, this.form, this.waybillDetail);
        break;
      case EDiversionsTypes.ORIGIN_TURNOVER:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.ORIGIN_TURNOVER]();
        break;
      case EDiversionsTypes.DESTINATION_TURNOVER:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.DESTINATION_TURNOVER]();
        break;
      case EDiversionsTypes.RESHIP:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.RESHIP]();
        this.addJunctions();
        NSDiversionsConstants.addCrossBorder(EDiversionsTypes.RESHIP, this.form, this.waybillDetail);
        break;
    }

    const personAuth = (this.form.get('personAuthorizing') as UntypedFormGroup);
    personAuth.addControl('customer', new UntypedFormControl(''));
    personAuth.addControl('customerName', new UntypedFormControl(''));

    this.form.patchValue(this.waybillDetail!);
  }

  private addJunctions(): void {
    if (this.waybillDetail?.route?.routes && this.waybillDetail.route.routes.length > 1) {
      this.routesFormArray.clear();
      this.waybillDetail.route.routes.forEach(() => {
        this.routesFormArray.push(NSDiversionsConstants.buildJunctionFormGroup(this.routesFormArray.length));
      });
    }
  }
}
